import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin } from 'lucide-react';
import { DealerLocation } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    h1 {
      margin: 0;
      font-size: 2rem;
      font-weight: 700;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LocationInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .location-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .location-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .date-navigation {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .current-week {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      min-width: 200px;
      text-align: center;
    }
  }
  
  .view-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ViewButton = styled.button<{ isActive?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${(props: { isActive?: boolean }) => props.isActive ? '#3b82f6' : '#d1d5db'};
  background: ${(props: { isActive?: boolean }) => props.isActive ? '#3b82f6' : 'white'};
  color: ${(props: { isActive?: boolean }) => props.isActive ? 'white' : '#374151'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${(props: { isActive?: boolean }) => props.isActive ? '#2563eb' : '#f3f4f6'};
    border-color: ${(props: { isActive?: boolean }) => props.isActive ? '#2563eb' : '#9ca3af'};
  }
`;

const CalendarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const TimeColumn = styled.div`
  padding: 12px 8px;
  border-right: 1px solid #e5e7eb;
  font-weight: 600;
  color: #6b7280;
  font-size: 0.75rem;
  text-align: center;
`;

const DayHeader = styled.div<{ isToday?: boolean }>`
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid #e5e7eb;
  background: ${(props: { isToday?: boolean }) => props.isToday ? '#eff6ff' : 'transparent'};
  
  &:last-child {
    border-right: none;
  }
  
  .day-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: { isToday?: boolean }) => props.isToday ? '#1d4ed8' : '#374151'};
    margin-bottom: 4px;
  }
  
  .day-number {
    font-size: 1.125rem;
    font-weight: 700;
    color: ${(props: { isToday?: boolean }) => props.isToday ? '#1d4ed8' : '#1f2937'};
  }
`;

const CalendarGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  overflow-y: auto;
`;

const TimeSlot = styled.div`
  padding: 8px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
  background: #fafafa;
  min-height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`;

const DayColumn = styled.div<{ isToday?: boolean }>`
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #f3f4f6;
  min-height: 60px;
  background: ${(props: { isToday?: boolean }) => props.isToday ? '#fefbff' : 'white'};
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { isToday?: boolean }) => props.isToday ? '#f0f9ff' : '#f9fafb'};
  }
  
  &:last-child {
    border-right: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LocationCalendarPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [location, setLocation] = useState<DealerLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'day' | 'week' | 'month'>('week');

  useEffect(() => {
    if (locationId) {
      loadLocationData();
    }
  }, [locationId]);

  const loadLocationData = async () => {
    if (!locationId || !user) return;

    try {
      setIsLoading(true);
      
      let foundLocation: DealerLocation | undefined;
      
      if (user.userType === 'dealer') {
        // For dealer users, get their dealer's locations
        const response = await apiService.getDealerLocations(user.dealerId);
        if (response.success && response.data) {
          foundLocation = response.data.find(loc => loc.id === locationId);
          
          // Check if staff user has access to this location
          if (foundLocation && (user as any).role === 'dealer_staff') {
            const userLocationIds = (user as any).locationIds || [];
            if (!userLocationIds.includes(locationId)) {
              toast.error('You do not have access to this location');
              navigate('/locations');
              return;
            }
          }
        }
      } else {
        // For admin users, try sessionStorage first, then search all dealers
        const dealerId = sessionStorage.getItem('currentDealerId');
        if (dealerId) {
          const response = await apiService.getDealerLocations(dealerId);
          if (response.success && response.data) {
            foundLocation = response.data.find(loc => loc.id === locationId);
          }
        }
        
        if (!foundLocation) {
          const dealersResponse = await apiService.getDealers();
          if (dealersResponse.success && dealersResponse.data) {
            for (const dealer of dealersResponse.data) {
              const locationsResponse = await apiService.getDealerLocations(dealer.id);
              if (locationsResponse.success && locationsResponse.data) {
                const location = locationsResponse.data.find(loc => loc.id === locationId);
                if (location) {
                  foundLocation = location;
                  sessionStorage.setItem('currentDealerId', dealer.id);
                  break;
                }
              }
            }
          }
        }
      }

      if (!foundLocation) {
        toast.error('Location not found');
        navigate('/dealers');
        return;
      }

      setLocation(foundLocation);
    } catch (error) {
      console.error('Error loading location data:', error);
      toast.error('Failed to load location data');
      navigate('/dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (user?.userType === 'dealer') {
      navigate('/locations');
    } else if (location) {
      const dealerId = sessionStorage.getItem('currentDealerId') || location.dealerId;
      navigate(`/dealers/${dealerId}/edit`);
    } else {
      navigate('/dealers');
    }
  };

  const getWeekDays = (date: Date): Date[] => {
    const week = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day; // First day is Sunday
    
    for (let i = 0; i < 7; i++) {
      const weekDay = new Date(date.getFullYear(), date.getMonth(), diff + i);
      week.push(weekDay);
    }
    
    return week;
  };

  const formatWeekRange = (date: Date): string => {
    const weekDays = getWeekDays(date);
    const start = weekDays[0];
    const end = weekDays[6];
    
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const year = start.getFullYear();
    
    if (start.getMonth() === end.getMonth()) {
      return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
    } else {
      return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`;
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const generateTimeSlots = (): string[] => {
    const slots = [];
    for (let hour = 7; hour <= 18; hour++) {
      slots.push(`${hour === 12 ? 12 : hour % 12}${hour < 12 ? 'am' : 'pm'}`);
      if (hour < 18) {
        slots.push(`${hour === 12 ? 12 : hour % 12}:30${hour < 12 ? 'am' : 'pm'}`);
      }
    }
    return slots;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!location) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Location not found
        </div>
      </PageContainer>
    );
  }

  const weekDays = getWeekDays(currentDate);
  const timeSlots = generateTimeSlots();

  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <BackButton onClick={handleBack}>
            <ArrowLeft />
            Back
          </BackButton>
          <h1>
            <CalendarIcon />
            Calendar
          </h1>
        </div>
        <div className="header-right">
          <ViewButton onClick={goToToday}>
            Today
          </ViewButton>
        </div>
      </PageHeader>

      <LocationInfo>
        <div className="location-name">
          <MapPin size={16} />
          {location.name}
        </div>
        <div className="location-details">
          Appointment calendar for this location
        </div>
      </LocationInfo>

      <CalendarControls>
        <div className="date-navigation">
          <NavButton onClick={() => navigateWeek('prev')}>
            <ChevronLeft />
          </NavButton>
          <div className="current-week">{formatWeekRange(currentDate)}</div>
          <NavButton onClick={() => navigateWeek('next')}>
            <ChevronRight />
          </NavButton>
        </div>
        <div className="view-controls">
          <ViewButton 
            isActive={currentView === 'day'} 
            onClick={() => setCurrentView('day')}
          >
            Day
          </ViewButton>
          <ViewButton 
            isActive={currentView === 'week'} 
            onClick={() => setCurrentView('week')}
          >
            Week
          </ViewButton>
          <ViewButton 
            isActive={currentView === 'month'} 
            onClick={() => setCurrentView('month')}
          >
            Month
          </ViewButton>
        </div>
      </CalendarControls>

      <CalendarContainer>
        <CalendarHeader>
          <TimeColumn></TimeColumn>
          {weekDays.map((day, index) => (
            <DayHeader key={index} isToday={isToday(day)}>
              <div className="day-name">
                {day.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className="day-number">
                {day.getDate()}
              </div>
            </DayHeader>
          ))}
        </CalendarHeader>

        <CalendarGrid>
          {timeSlots.map((time, timeIndex) => (
            <React.Fragment key={timeIndex}>
              <TimeSlot>{time}</TimeSlot>
              {weekDays.map((day, dayIndex) => (
                <DayColumn 
                  key={`${timeIndex}-${dayIndex}`} 
                  isToday={isToday(day)}
                />
              ))}
            </React.Fragment>
          ))}
        </CalendarGrid>
      </CalendarContainer>
    </PageContainer>
  );
};

export default LocationCalendarPage;
